import type { ReactNode } from "react";

/**
 * Compact circular progress meter for section onboarding. Renders as an SVG
 * ring with either the percentage (default) or arbitrary `children` (e.g. a
 * section icon, making it a progress *medallion*) in the center.
 *
 * The unfilled track is the accent at low opacity when an `accent` is given,
 * so the ring stays visible even at 0% (a bare `text-border` track vanishes
 * on the dark canvas). Stays in lockstep with light + dark mode.
 */

interface SectionProgressRingProps {
  /** 0..100 inclusive. */
  value: number;
  /** Pixel diameter. Default 48. */
  size?: number;
  /** Stroke thickness. Default 4. */
  stroke?: number;
  /**
   * Accent color for the filled arc, the (tinted) track, and the center
   * percentage. Pass a section's hex (`sectionColor(slug)`) to tie the ring
   * to its section. Defaults to the foreground/border tokens when omitted.
   */
  accent?: string;
  /**
   * Center content. When provided, renders instead of the percentage —
   * pass a section icon to turn the ring into an identity medallion.
   */
  children?: ReactNode;
}

export function SectionProgressRing({
  value,
  size = 48,
  stroke = 4,
  accent,
  children,
}: SectionProgressRingProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - clamped / 100);
  return (
    <div
      role="img"
      aria-label={`${Math.round(clamped)} percent complete`}
      className="relative shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={accent ?? "currentColor"}
          strokeWidth={stroke}
          fill="none"
          strokeOpacity={accent ? 0.22 : undefined}
          className={accent ? undefined : "text-border"}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={accent ?? "currentColor"}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="text-foreground transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center whitespace-nowrap type-subtext tabular-nums"
        style={accent ? { color: accent } : undefined}
      >
        {children ?? (
          <>
            {Math.round(clamped)}
            {/* % inherits the accent (not a separate muted token, which read
                as a mismatched grey), held back with opacity so the number
                still leads. */}
            <span className="opacity-60">%</span>
          </>
        )}
      </span>
    </div>
  );
}
